import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import {
  PRECIO_OFICIAL, PALETTE, SPANISH_MONTHS_LONG,
  fmtCurrency, fmtPct, titleCase,
} from './chartShared';
import './WeeklyAverageYearChart.css';
import './PriceRangeChart.css';

const REFRESH_MS = 5 * 60 * 1000;

// Convert ISO week number + year to BusinessDay of that week's Monday
const isoWeekToBusinessDay = (weekNum, year) => {
  const jan4 = new Date(year, 0, 4);
  const jan4Dow = (jan4.getDay() + 6) % 7; // Mon=0, Sun=6
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - jan4Dow);
  const target = new Date(week1Monday);
  target.setDate(week1Monday.getDate() + (weekNum - 1) * 7);
  return {
    year: target.getFullYear(),
    month: target.getMonth() + 1,
    day: target.getDate(),
  };
};

// Inverse: BusinessDay → ISO week number (used for tick labels & tooltip)
const businessDayToISOWeek = (bd) => {
  const d = new Date(bd.year, bd.month - 1, bd.day);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const fmtWeekLabel = (bd) => {
  const wk = businessDayToISOWeek(bd);
  return `Semana ${wk} · del ${bd.day} de ${SPANISH_MONTHS_LONG[bd.month - 1]} ${bd.year}`;
};

const WeeklyAverageYearChart = () => {
  const { t } = useTranslation();
  const [zone, setZone] = useState('norte');
  const [weeklyAvgData, setWeeklyAvgData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setNowTick] = useState(0);

  const chartContainerRef = useRef(null);
  const tooltipRef        = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = async (silent = false) => {
      const token = localStorage.getItem('access_token');
      if (!token) { setError(t('auth_required')); setLoading(false); return; }
      if (!silent) setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/prices/weekly-average-year/?zone=${zone}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!Array.isArray(response.data) || response.data.length === 0) {
          if (!silent) setError(t('no_data_available'));
          setWeeklyAvgData([]);
        } else {
          setWeeklyAvgData(response.data);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching weekly average:', err);
        if (!silent) setError(t('weekly_average_error'));
      } finally {
        if (!silent && !cancelled) setLoading(false);
      }
    };
    run();
    const interval = setInterval(() => run(true), REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [t, zone]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(n => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Convert week-number rows to BusinessDay-time series
  const series = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return weeklyAvgData
      .map(d => {
        const week = parseInt(d.week, 10);
        const yr   = parseInt(d.year, 10) || currentYear;
        const v    = parseFloat(d.avg_price);
        if (!week || Number.isNaN(v)) return null;
        const time = isoWeekToBusinessDay(week, yr);
        return { time, value: v, week, year: yr };
      })
      .filter(Boolean)
      .sort((a, b) =>
        a.time.year - b.time.year ||
        a.time.month - b.time.month ||
        a.time.day - b.time.day
      );
  }, [weeklyAvgData]);

  const stats = useMemo(() => {
    if (!series.length) return null;
    const last = series[series.length - 1];
    const avg = series.reduce((s, p) => s + p.value, 0) / series.length;
    const minPt = series.reduce((a, b) => (b.value < a.value ? b : a));
    const maxPt = series.reduce((a, b) => (b.value > a.value ? b : a));
    const diffOficial    = avg - PRECIO_OFICIAL;
    const diffOficialPct = (diffOficial / PRECIO_OFICIAL) * 100;
    return { last, avg, minPt, maxPt, diffOficial, diffOficialPct };
  }, [series]);

  // Chart lifecycle ─────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current || !series.length) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor:  PALETTE.lightGray,
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: '#f0f1f5' },
      },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.18, bottom: 0.15 } },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        tickMarkFormatter: (time) => {
          if (time && typeof time === 'object' && 'year' in time) {
            // Eje en formato "Wk##" — semana ISO del año
            return `Wk${businessDayToISOWeek(time)}`;
          }
          return '';
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: PALETTE.lightGray, width: 1, style: LineStyle.Dotted, labelVisible: false },
        horzLine: { color: PALETTE.lightGray, width: 1, style: LineStyle.Dotted, labelVisible: false },
      },
      handleScroll: false,
      handleScale:  false,
    });

    const areaSeries = chart.addAreaSeries({
      lineColor:    PALETTE.green,
      lineWidth:    3,
      topColor:     'rgba(23,198,113,0.32)',
      bottomColor:  'rgba(23,198,113,0)',
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat:  { type: 'price', precision: 2, minMove: 0.01 },
    });
    areaSeries.setData(series.map(({ time, value }) => ({ time, value })));

    areaSeries.createPriceLine({
      price: PRECIO_OFICIAL,
      color: PALETTE.lightGray,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: 'Oficial',
    });

    if (stats) {
      const maxWk = businessDayToISOWeek(stats.maxPt.time);
      const minWk = businessDayToISOWeek(stats.minPt.time);
      areaSeries.setMarkers([
        { time: stats.maxPt.time, position: 'aboveBar', color: PALETTE.green, shape: 'arrowDown', text: `Wk${maxWk} · ${fmtCurrency(stats.maxPt.value)}` },
        { time: stats.minPt.time, position: 'belowBar', color: PALETTE.red,   shape: 'arrowUp',   text: `Wk${minWk} · ${fmtCurrency(stats.minPt.value)}` },
      ]);
    }

    chart.timeScale().fitContent();

    chart.subscribeCrosshairMove(param => {
      const tip = tooltipRef.current;
      const container = chartContainerRef.current;
      if (!tip || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0 || param.point.x > w || param.point.y > h) {
        tip.style.display = 'none';
        return;
      }
      const data = param.seriesData.get(areaSeries);
      if (!data) { tip.style.display = 'none'; return; }
      const tm = data.time;
      if (!(tm && typeof tm === 'object' && 'year' in tm)) { tip.style.display = 'none'; return; }
      const label = titleCase(fmtWeekLabel(tm));
      const diff = data.value - PRECIO_OFICIAL;
      const diffLabel = diff >= 0
        ? `<span style="color:${PALETTE.green}">${fmtPct((diff / PRECIO_OFICIAL) * 100)} sobre el oficial</span>`
        : `<span style="color:${PALETTE.red}">${fmtPct((diff / PRECIO_OFICIAL) * 100)} bajo el oficial</span>`;
      tip.innerHTML = `
        <div class="prc-tip__date">${label}</div>
        <div class="prc-tip__price">${fmtCurrency(data.value)}</div>
        <div class="prc-tip__diff">${diffLabel}</div>
      `;
      tip.style.display = 'block';
      const tipW = tip.clientWidth;
      const tipH = tip.clientHeight;
      let x = param.point.x + 16;
      let y = param.point.y - tipH - 16;
      if (x + tipW > w) x = param.point.x - tipW - 16;
      if (y < 0) y = param.point.y + 16;
      tip.style.left = `${x}px`;
      tip.style.top  = `${y}px`;
    });

    const handleResize = () => {
      if (!chartContainerRef.current) return;
      chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      chart.timeScale().fitContent();
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [series, stats]);

  if (loading) return <div className="loading">{t('loading')}...</div>;
  if (error)   return <div className="error">{error}</div>;
  if (series.length === 0) return <div className="loading">{t('no_data_available')}</div>;

  // Auto-insight for yearly view
  const insight = stats && (() => {
    const aboveCount = series.filter(p => p.value >= PRECIO_OFICIAL).length;
    const pct = (aboveCount / series.length) * 100;
    if (pct >= 70) return { icon: '🌱', text: `${aboveCount} de las ${series.length} semanas analizadas estuvieron sobre el precio oficial.` };
    if (pct <= 30) return { icon: '⚠️', text: `Solo ${aboveCount} de ${series.length} semanas estuvieron sobre el precio oficial este año.` };
    return { icon: '📊', text: `${aboveCount} de ${series.length} semanas el promedio superó el precio oficial.` };
  })();

  return (
    <div className="weekly-average-container" style={{ marginTop: 20 }}>
      <div className="zone-selector-row">
        <h2>{t('weekly_average_year_title')}</h2>
        <div className="zone-tabs">
          <button className={`zone-tab ${zone === 'norte' ? 'zone-tab--active' : ''}`} onClick={() => setZone('norte')}>{t('zona_norte')}</button>
          <button className={`zone-tab ${zone === 'sur'   ? 'zone-tab--active' : ''}`} onClick={() => setZone('sur')}>{t('zona_sur')}</button>
        </div>
      </div>

      <div className="prc-card">
        {stats && (
          <div className="prc-hero">
            <div className="prc-hero__main">
              <div className="prc-hero__value">{fmtCurrency(stats.avg)}</div>
              <div className="prc-hero__caption">promedio del año</div>
            </div>
            <div className="prc-hero__deltas">
              <span className="prc-delta">
                Última semana: <strong style={{ color: PALETTE.navy }}>{fmtCurrency(stats.last.value)}</strong>
              </span>
              <span className={`prc-delta ${stats.diffOficial >= 0 ? 'prc-delta--up' : 'prc-delta--down'}`}>
                {stats.diffOficial >= 0
                  ? `${fmtPct(stats.diffOficialPct)} sobre el precio oficial 🌱`
                  : `${fmtPct(stats.diffOficialPct)} bajo el precio oficial ⚠️`}
              </span>
            </div>
          </div>
        )}

        <div className="prc-chart-wrap">
          <div className="prc-chart" ref={chartContainerRef} />
          <div className="prc-tip" ref={tooltipRef} />
        </div>

        {insight && (
          <div className="prc-insight">
            <span className="prc-insight__icon">{insight.icon}</span>
            <span className="prc-insight__text">{insight.text}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyAverageYearChart;
